import { useQuery } from '@tanstack/react-query';
import { getContext } from '@/context';
import { fetchUserAvatar, DEFAULT_AVATAR } from '@/requests/user/userProfile';
import { getUserTier } from '@/features/credits';
import useAuth from './useAuth';

/**
 * Identity + avatar + tier for the signed-in user. `username`/`userId` are read
 * synchronously from the host auth model; the avatar and tier are fetched via
 * React Query (keyed by `userId`) so they're cached across remounts — page
 * redirections no longer re-fetch the avatar on every mount.
 */
const useUserProfile = () => {
  const { isAuthorized } = useAuth();

  const authState = isAuthorized ? getContext().general.auth.getState() : null;
  const username = authState?.username ?? '';
  const userId = authState?.id ?? null;

  const { data } = useQuery({
    queryKey: ['userAvatar', userId],
    queryFn: () =>
      Promise.all([fetchUserAvatar(userId as number), getUserTier()]),
    enabled: Boolean(isAuthorized && userId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    isAuthorized: isAuthorized ?? false,
    username,
    userId,
    avatarSrc: data?.[0] ?? DEFAULT_AVATAR,
    tierId: data?.[1]?.id ?? null,
  };
};

export default useUserProfile;
